<template>
  <div :class="['selection', { 'is-open': showDropdown }, { 'has-error': error }]">
    <button
      :for="`select-${id}`"
      class="selection-toggle"
      @click.prevent="toggle"
      :disabled="disabled"
    >
      <span class="selection-toggle-text">{{ selected ? selected.label : placeholder }}</span>
      <sprite
        v-if="showDropdown"
        :type="'chevron-up'"
        :width="10"
        :height="5"
      />
      <sprite
        v-else
        :type="'chevron-down'"
        :width="10"
        :height="5"
      />
    </button>

    <span
      v-if="error"
      class="selection-error"
    >
      {{ error }}
    </span>

    <div
      v-show="showDropdown"
      class="selection-panel"
    >
      <button
        :class="['selection-option', { 'is-selected': !selected }]"
        @click.prevent="onOption(null)"
        :disabled="!canBeNull"
      >
        {{ placeholder }}
      </button>

      <button
        v-for="(option, index) in options"
        :key="`select-${option.value}-${index}`"
        :class="['selection-option', { 'is-end': option.isEnd }, { 'is-selected': selected && option.value === selected.value }]"
        @click.prevent="onOption(option)"
      >
        {{ option.label }}
      </button>
    </div>
  </div>
</template>

<script>
import Sprite from '@/components/components/Sprite.vue';

export default {
  components: {
    Sprite
  },
  props: {
    id: {
      type: String,
      required: true
    },
    options: {
      type: Array,
      required: true
    },
    multiple: {
      type: Boolean,
      default: false
    },
    selected: Object,
    placeholder: {
      type: String,
      default: 'Selectionnez une option'
    },
    canBeNull: {
      type: Boolean,
      default: true
    },
    label: String,
    error: String,
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showDropdown: false,
      windowClick: this.onWindowClick.bind(this)
    };
  },
  methods: {
    toggle() {
      if (this.showDropdown) {
        this.close();
        return;
      }
      this.open();
    },
    open() {
      this.showDropdown = true;
      window.addEventListener('click', this.windowClick);
    },
    close() {
      this.showDropdown = false;
      window.removeEventListener('click', this.windowClick);
    },
    onOption(option) {
      if (!option) {
        this.$emit('option', option);
        this.close();
        return;
      }

      if (!this.selected || option.value !== this.selected.value) {
        this.$emit('option', option);
      }

      this.close();
    },
    onWindowClick(e) {
      const { target } = e;
      const isChild = this.isDescendant(target);
      if (!isChild) {
        this.close();
      }
    },
    isDescendant(child) {
      let node = child.parentNode;
      while (node != null) {
        if (node === this.$el) {
          return true;
        }
        node = node.parentNode;
      }

      return false;
    }
  }
};
</script>

<style lang="scss" scoped>
.selection {
  position: relative;
  z-index: 1;

  &.has-error {
    .selection-toggle {
      border-color: $primary;
    }
  }

  &-toggle {
    @include font('secondary', 'regular');
    @include padding(11px 20px 11px 11px);
    display: block;
    position: relative;
    width: 100%;
    border: 1px solid rgba($white, .1);
    border-radius: 6px;
    background-color: $black-light;
    color: $white;
    font-size: rem(14px);
    line-height: (16 / 14);
    text-align: left;
    outline: none;

    &[disabled] {
      opacity: 0.4;
    }

    &-text {
      @include line-clamp(1, 24px, false);
    }

    .svg {
      @include position(absolute, 50% 10px null null);
      transform: translateY(-50%);
      fill: $white;
    }
  }

  &-error {
    @include padding(12px null null);
    display: block;
    color: $primary;
    font-size: rem(12px);
    line-height: 1;
  }

  &-panel {
    @include position(absolute, calc(100% + 4px) null null 0);
    @include padding(2px null);
    @include size(100%, auto);
    display: flex;
    flex-direction: column;
    max-height: 175px;
    overflow: auto;
    border: 1px solid rgba($white, .1);
    border-radius: 6px;
    background-color: $black-light;
    box-shadow: 0 4px 16px rgba(126, 133, 142, .16);
  }

  &-option {
    @include font('secondary', 'regular');
    @include padding(10px 8px);
    flex-shrink: 0;
    color: $white;
    font-size: rem(14px);
    line-height: (16 / 14);
    text-align: left;

    &[disabled] {
      color: rgba($white, .6);
      pointer-events: none;
    }

    &.is-end {
      position: relative;
      padding-bottom: 16px;

      &::after {
        @include position(absolute, null null 0 8px);
        @include size(calc(100% - 16px), 1px);
        content: '';
        background-color: rgba($white, .1);
      }

      + .selection-option {
        padding-top: 16px;
      }
    }

    &.is-selected {
      color: $primary;
    }
  }
}
</style>
